import React from "react";

import { FileText } from "lucide-react";

import {
  Account,
  AccountTeamsBatchEntry,
  AccountTeamsByAccountId,
} from "./types";
import { formatAssessmentDateUtc } from "../../../../Utils/Helper/assessmentDateUtils";

const getStakeholderLists = (
  account: Account,
  teams?: AccountTeamsBatchEntry
) => {
  const assignedSponsors = teams?.assignedSponsors?.length
    ? teams.assignedSponsors
    : (account.executives ?? []);

  return {
    assignedSponsors,
    accountTeam: teams?.accountTeam ?? [],
    otherExecutives: teams?.sponsors ?? [],
  };
};

const getSelectedByGroup = (
  account: Account,
  selectedIds: string[],
  teams?: AccountTeamsBatchEntry
) => {
  const lists = getStakeholderLists(account, teams);

  const pickNames = (items: { id: string; name: string }[]) =>
    items.filter(item => selectedIds.includes(item.id)).map(item => item.name);

  return {
    assignedSponsors: pickNames(lists.assignedSponsors),
    accountTeam: pickNames(lists.accountTeam),
    otherExecutives: pickNames(lists.otherExecutives),
  };
};

interface StakeholderGroupProps {
  label: string;
  names: string[];
}

const StakeholderGroup = ({ label, names }: StakeholderGroupProps) => {
  if (names.length === 0) return null;

  return (
    <div className="mt-1">
      <span className="text-xs font-medium text-gray-600">{label}: </span>
      <span className="text-xs text-gray-500">{names.join(", ")}</span>
    </div>
  );
};

interface Step5Props {
  title: string;
  templateName: string;
  dueDate: string;
  selectedAccounts: Account[];
  selectedAccountIds: string[];
  existingAccountIds?: string[];
  accountExecutives: Record<string, string[]>;
  accountTeamsByAccountId?: AccountTeamsByAccountId;
  isOngoingEdit?: boolean;
}

const Step5ReviewLaunch: React.FC<Step5Props> = ({
  title,
  templateName,
  dueDate,
  selectedAccounts,
  selectedAccountIds,
  existingAccountIds = [],
  accountExecutives,
  accountTeamsByAccountId = {},
  isOngoingEdit = false,
}) => {
  const newAccountsCount = selectedAccountIds.filter(
    id => !existingAccountIds.includes(id)
  ).length;

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Review & Launch Assessment
        </h3>
        <p className="text-gray-600">
          Review your assessment configuration before launching
        </p>
      </div>

      <div className="bg-gray-50 rounded-lg p-6 space-y-4">
        <div>
          <h4 className="font-medium text-gray-900 mb-2">Assessment Details</h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Title:</span>
              <span className="text-gray-900">{title}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Template:</span>
              <span className="text-gray-900">{templateName}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Due Date:</span>
              <span className="text-gray-900">
                {dueDate
                  ? formatAssessmentDateUtc(dueDate, "US_SHORT")
                  : "Not set"}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Target Accounts:</span>
              <span className="text-gray-900">
                {selectedAccountIds.length} accounts
                {isOngoingEdit ? ` (${newAccountsCount} New)` : ""}
              </span>
            </div>
          </div>
        </div>

        <div>
          <h4 className="font-medium text-gray-900 mb-2">Selected Accounts</h4>
          <div className="space-y-3 text-sm max-h-48 overflow-y-auto">
            {selectedAccountIds.map(accountId => {
              const account = selectedAccounts.find(a => a.id === accountId);
              const selectedIds = accountExecutives[accountId] ?? [];

              if (!account) return null;

              const teams = accountTeamsByAccountId[accountId];
              const selectedByGroup = getSelectedByGroup(
                account,
                selectedIds,
                teams
              );

              return (
                <div
                  key={accountId}
                  className="border-l-2 border-purple-200 pl-3 pb-2"
                >
                  <div className="flex justify-between gap-2">
                    <span className="font-medium text-gray-800">
                      {account.name}
                    </span>
                    <span className="shrink-0 text-gray-900">
                      {selectedIds.length} selected
                    </span>
                  </div>

                  <StakeholderGroup
                    label="Assigned Sponsors"
                    names={selectedByGroup.assignedSponsors}
                  />
                  <StakeholderGroup
                    label="Account Team"
                    names={selectedByGroup.accountTeam}
                  />
                  <StakeholderGroup
                    label="Other Executives"
                    names={selectedByGroup.otherExecutives}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <FileText className="h-5 w-5 text-blue-600 mt-0.5" />
          <div className="text-sm">
            {isOngoingEdit ? (
              <>
                <p className="text-blue-800 font-medium">
                  Ready to add accounts
                </p>
                <p className="text-blue-700 mt-1">
                  Only the newly selected accounts and their executive sponsors
                  will be added. Existing accounts are unchanged.
                </p>
              </>
            ) : (
              <>
                <p className="text-blue-800 font-medium">Ready to Launch</p>
                <p className="text-blue-700 mt-1">
                  This assessment will be sent to all executives assigned to the
                  selected accounts. They will receive notifications and can
                  complete the assessment through their dashboard.
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Step5ReviewLaunch;
